import { useState, useContext, useCallback } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, User, UserPlus, UserCheck, Loader2, Home } from 'lucide-react';
import PlayerFollowButton from '../components/PlayerFollowButton';
import { useSafeBack } from '../hooks/useSafeBack';

const PlayerSearch = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const res = await apiClient.get(`/users/search?username=${encodeURIComponent(searchQuery.trim())}`);
      setResults(res.data?.users || res.data || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setResults([]);
      } else {
        // Handle validation errors (422) which return an array of error objects
        const detail = err.response?.data?.detail;
        let errorMsg = 'Search failed. Please try again.';
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
        }
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleBack = () => {
    goBack();
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="player-search-page">
      {/* Header with back button */}
      <div
        className="sticky top-0 z-30 bg-white border-b border-zinc-100 px-4 pb-3"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-1.5 -ml-1.5 rounded-lg hover:bg-zinc-100 transition-colors"
            data-testid="player-search-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-700" />
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="p-1.5 rounded-lg hover:bg-zinc-100 transition-colors"
            data-testid="nav-home-btn"
            aria-label="Dashboard"
          >
            <Home className="w-4 h-4 text-zinc-500" />
          </button>
          <h1 className="text-lg font-semibold text-zinc-900">Find Players</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Search bar */}
        <div className="flex gap-2" data-testid="player-search-bar">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by username"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              autoFocus
              data-testid="player-search-input"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !searchQuery.trim()}
            className="px-4 py-2.5 bg-zinc-900 text-white rounded-lg text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="player-search-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="player-search-error">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12" data-testid="player-search-loading">
            <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
          </div>
        )}

        {/* Results */}
        {!loading && results.length > 0 && (
          <div className="space-y-2" data-testid="player-search-results">
            <p className="text-xs text-zinc-400 font-medium uppercase tracking-wide">{results.length} result{results.length !== 1 ? 's' : ''}</p>
            {results.map((player) => (
              <div
                key={player.id || player.username}
                className="bg-white rounded-lg border border-zinc-100 p-3 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                data-testid={`player-result-${player.username}`}
              >
                <div
                  className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  onClick={() => navigate(`/player/${player.username}`)}
                >
                  <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{player.display_name || player.username}</p>
                    <p className="text-xs text-zinc-400">@{player.username}</p>
                  </div>
                </div>
                {user && player.username !== user.username && (
                  <PlayerFollowButton
                    userId={player.id}
                    username={player.username}
                    isFollowing={player.is_following || false}
                    isRequested={player.is_requested || false}
                    size="sm"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && searched && results.length === 0 && !error && (
          <div className="text-center py-12" data-testid="player-search-empty">
            <User className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-zinc-600">No players found</p>
            <p className="text-xs text-zinc-400 mt-1">Try a different username</p>
          </div>
        )}

        {/* Initial state */}
        {!loading && !searched && (
          <div className="text-center py-12" data-testid="player-search-initial">
            <Search className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">Search for players by their username</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSearch;
